import { useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { BANK_EDIT_ROLES, SELLER_ENTITY_LABELS_RU, type BankLineDto } from '@crm/shared'
import {
  autoAllocateBankLines,
  deleteBankStatement,
  getBankStatement,
} from '../../shared/api/bank.api'
import { apiErrorText } from '../../shared/api/errorText'
import { useAuthStore } from '../auth/authStore'
import { PageLoader } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { confirm } from '../../shared/ui/confirmStore'
import { toast } from '../../shared/ui/toastStore'
import { BankLinesTable } from './BankLinesTable'
import {
  BANK_OPEN_LINES_KEY,
  BANK_STATEMENTS_KEY,
  bankStatementKey,
  formatBankMoney,
  formatBankPeriod,
} from './bankFormat'

type Tab = 'OPEN' | 'ALLOCATED' | 'SKIPPED' | 'ALL'
const TABS: Array<{ id: Tab; label: string }> = [
  { id: 'OPEN', label: 'К разносу' },
  { id: 'ALLOCATED', label: 'Разнесено' },
  { id: 'SKIPPED', label: 'Не учитываем' },
  { id: 'ALL', label: 'Все строки' },
]

const isOpen = (l: BankLineDto) => l.status === 'PENDING' || l.status === 'PARTIAL'
const inTab = (l: BankLineDto, tab: Tab): boolean =>
  tab === 'ALL' ||
  (tab === 'OPEN' && isOpen(l)) ||
  (tab === 'ALLOCATED' && (l.status === 'ALLOCATED' || l.status === 'PARTIAL')) ||
  (tab === 'SKIPPED' && l.status === 'SKIPPED')

/** Страница одной выписки: итоги банка, строки и разнос поступлений по заказам. */
export function BankStatementPage() {
  const { id = '' } = useParams<{ id: string }>()
  const role = useAuthStore((s) => s.user?.role)
  const canEdit = !!role && (BANK_EDIT_ROLES as string[]).includes(role)
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('OPEN')
  const [running, setRunning] = useState(false)
  const navigate = useNavigate()
  const query = useQuery({
    queryKey: [...bankStatementKey(id)],
    queryFn: () => getBankStatement(id),
  })

  const lines = useMemo(() => query.data?.lines ?? [], [query.data])
  const shown = useMemo(() => lines.filter((l) => inTab(l, tab)), [lines, tab])
  const counts = useMemo(
    () =>
      Object.fromEntries(
        TABS.map((t) => [t.id, lines.filter((l) => inTab(l, t.id)).length]),
      ) as Record<Tab, number>,
    [lines],
  )
  const openSum = useMemo(() => lines.filter(isOpen).reduce((s, l) => s + l.remaining, 0), [lines])
  const allocatedSum = useMemo(() => lines.reduce((s, l) => s + l.allocatedAmount, 0), [lines])

  const runAuto = async () => {
    setRunning(true)
    try {
      const res = await autoAllocateBankLines(id)
      for (const key of [bankStatementKey(id), BANK_OPEN_LINES_KEY, BANK_STATEMENTS_KEY]) {
        void qc.invalidateQueries({ queryKey: [...key] })
      }
      toast.success(
        res.allocated > 0
          ? `Разнесено автоматически: ${res.allocated}; на ручной разбор осталось: ${res.left}`
          : 'Новых совпадений нет — оставшиеся строки нужно разобрать вручную',
      )
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось запустить авторазнос'))
    } finally {
      setRunning(false)
    }
  }

  const remove = async () => {
    const allocated = lines.filter((l) => l.allocations.length > 0).length
    const ok = await confirm({
      title: 'Удалить выписку?',
      message:
        `Будут удалены все её строки (${lines.length}).` +
        (allocated > 0
          ? ` Разнос по ${allocated} из них отменится: оплаты из банка уйдут из заказов, статусы заказов пересчитаются.`
          : '') +
        ' Тот же файл потом можно загрузить заново.',
      confirmText: 'Удалить выписку',
      danger: true,
    })
    if (!ok) return
    setRunning(true)
    try {
      await deleteBankStatement(id)
      for (const key of [BANK_OPEN_LINES_KEY, BANK_STATEMENTS_KEY]) {
        void qc.invalidateQueries({ queryKey: [...key] })
      }
      qc.removeQueries({ queryKey: [...bankStatementKey(id)] })
      toast.success('Выписка удалена')
      void navigate('/bank')
    } catch (e) {
      toast.error(apiErrorText(e, 'Не удалось удалить выписку'))
      setRunning(false)
    }
  }

  if (query.isLoading) return <PageLoader label="Загружаю выписку…" />
  if (query.isError || !query.data) return <ErrorMessage error={query.error} />
  const st = query.data

  const kpis: Array<[string, string]> = [
    ['Остаток на начало', formatBankMoney(st.openingBalance)],
    ['Поступило', formatBankMoney(st.totalIn)],
    ['Списано', formatBankMoney(st.totalOut)],
    ['Разнесено по заказам', formatBankMoney(allocatedSum)],
    ['К разносу', `${counts.OPEN} · ${formatBankMoney(openSum)}`],
  ]

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">
        <Link to="/bank" className="hover:text-gray-900">
          Банк
        </Link>
        <span className="mx-2">›</span>
        <span className="text-gray-900">
          Выписка {formatBankPeriod(st.periodFrom, st.periodTo)}
        </span>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">
            Выписка за {formatBankPeriod(st.periodFrom, st.periodTo)}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {st.account.entityId ? (
              SELLER_ENTITY_LABELS_RU[st.account.entityId]
            ) : (
              <span className="text-orange-600">
                Юрлицо счёта не указано — по номеру счёта программа не ищет
              </span>
            )}
            {' · счёт '}
            <span className="font-mono">{st.account.accountNumber}</span>
            {st.account.bankName ? ` · ${st.account.bankName}` : ''}
          </p>
          {st.linesNew < st.linesTotal && (
            <p className="mt-1 text-xs text-gray-500">
              В файле было строк: {st.linesTotal}; {st.linesTotal - st.linesNew} из них уже были
              загружены раньше и здесь не повторяются.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <button
              type="button"
              disabled={running}
              onClick={() => void remove()}
              className="px-3 py-1.5 border border-red-200 bg-white text-sm text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-60"
            >
              Удалить выписку
            </button>
          )}
          {canEdit && counts.OPEN > 0 && (
            <button
              type="button"
              disabled={running}
              onClick={() => void runAuto()}
              title="Провести строки, для которых программа уверена: клиент найден по ИНН и номер счёта совпал"
              className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-60"
            >
              {running ? 'Разношу…' : 'Разнести автоматически'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {kpis.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-gray-200 bg-white p-4">
            <div className="text-lg font-semibold text-gray-900 tabular-nums">{value}</div>
            <div className="mt-1 text-xs text-gray-500">{label}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
              tab === t.id
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-gray-200 bg-white text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label} ({counts[t.id]})
          </button>
        ))}
      </div>

      <BankLinesTable
        lines={shown}
        canEdit={canEdit}
        storageKey="crm_bank_lines_cols_v2"
        className="h-[calc(100vh-420px)] min-h-[320px]"
        invalidateKeys={[bankStatementKey(id)]}
      />
    </div>
  )
}
